const CHUNK_LOAD_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /loading chunk [\w-]+ failed/i,
  /chunkloaderror/i,
];

const RECOVERY_QUERY_PARAM = 'pp_shell_refresh';
const RECOVERY_STORAGE_PREFIX = 'poolparty:chunk-load-recovery';

declare const __BUILD_SHA__: string;

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export function isRecoverableChunkLoadError(error: unknown): boolean {
  const message = getErrorMessage(error);
  const name = error instanceof Error ? error.name : '';
  const stack = error instanceof Error ? error.stack || '' : '';
  const text = `${name}\n${message}\n${stack}`;

  return CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(text));
}

function getCurrentBuildId(): string {
  try {
    return typeof __BUILD_SHA__ !== 'undefined' ? __BUILD_SHA__ : 'dev';
  } catch {
    return 'dev';
  }
}

function recoveryStorageKey(url: URL, buildId: string): string {
  return `${RECOVERY_STORAGE_PREFIX}:${buildId}:${url.origin}${url.pathname}`;
}

export function buildChunkLoadRecoveryUrl(currentHref: string, stamp: number | string = Date.now()): string {
  const url = new URL(currentHref);
  url.searchParams.set(RECOVERY_QUERY_PARAM, String(stamp));
  return url.toString();
}

export interface ChunkLoadRecoveryOptions {
  force?: boolean;
  delayMs?: number;
  location?: Pick<Location, 'href' | 'replace'>;
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  setTimeoutFn?: typeof setTimeout;
  buildId?: string;
  stamp?: number | string;
}

export function scheduleChunkLoadRecovery(options: ChunkLoadRecoveryOptions = {}): boolean {
  if (typeof window === 'undefined' && (!options.location || !options.storage)) return false;

  const locationRef = options.location || window.location;
  const storageRef = options.storage || window.sessionStorage;
  const timeoutRef = options.setTimeoutFn || setTimeout;
  const buildId = options.buildId || getCurrentBuildId();
  const currentUrl = new URL(locationRef.href);
  const storageKey = recoveryStorageKey(currentUrl, buildId);

  try {
    if (!options.force && storageRef.getItem(storageKey)) {
      return false;
    }
  } catch {
    // Continue without loop protection when storage is unavailable.
  }

  try {
    storageRef.setItem(storageKey, JSON.stringify({
      at: new Date().toISOString(),
      href: currentUrl.toString(),
      buildId,
    }));
  } catch {
    // Storage can be unavailable in private browsing or constrained webviews.
  }

  const nextHref = buildChunkLoadRecoveryUrl(currentUrl.toString(), options.stamp ?? Date.now());
  timeoutRef(() => locationRef.replace(nextHref), options.delayMs ?? 250);
  return true;
}
